/**
 * 経過比較グラフ (F-15)
 *
 * 患者の検査結果を時系列で表示するSVGグラフコンポーネント。
 * 外部ライブラリを使わず、SVGで描画する。
 *
 * - X軸: 検査日時
 * - Y軸: 平均偏差 (mm)
 * - 異常閾値ラインを点線で表示
 * - ホバー/タップでツールチップ表示
 * - レスポンシブ対応
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// ========================================
// 型定義
// ========================================

export interface DeviationChartDataPoint {
  date: string;
  averageDeviationMm: number;
  averageDeviationPercent: number;
  abnormalTrials: number;
  totalTrials: number;
}

export interface DeviationChartProps {
  data: DeviationChartDataPoint[];
  /** 異常判定閾値（%）。この値に対応するmmラインは描画しないが、%値として凡例に表示 */
  threshold?: number;
}

// ========================================
// 定数
// ========================================

const PADDING = { top: 28, right: 24, bottom: 64, left: 56 };
const POINT_RADIUS = 5;
const POINT_HOVER_RADIUS = 7;
const GRID_COLOR = '#e5e7eb';
const LINE_COLOR = '#3b82f6';
const POINT_COLOR = '#2563eb';
const ABNORMAL_POINT_COLOR = '#dc2626';
const ZERO_LINE_COLOR = '#9ca3af';
const MIN_Y_TICKS = 5;

// ========================================
// ヘルパー関数
// ========================================

/**
 * Y軸の目盛りを「きりのいい数値」で生成する
 */
function computeYTicks(minVal: number, maxVal: number): number[] {
  const range = maxVal - minVal;
  if (range === 0) {
    // 全て同じ値の場合、前後にマージンを設ける
    const v = minVal;
    return [v - 2, v - 1, v, v + 1, v + 2];
  }

  // 目盛り間隔を決定（1, 2, 5, 10, 20, 50...のパターン）
  const roughStep = range / (MIN_Y_TICKS - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;

  let niceStep: number;
  if (residual <= 1.5) niceStep = 1 * magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMin = Math.floor(minVal / niceStep) * niceStep;
  const niceMax = Math.ceil(maxVal / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + niceStep * 0.01; v += niceStep) {
    ticks.push(parseFloat(v.toFixed(6)));
  }
  return ticks;
}

/**
 * 日付をフォーマットする（M/D形式）
 */
function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * 日付をフォーマットする（YYYY/MM/DD HH:mm形式）
 */
function formatDateFull(dateStr: string): string {
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
}

// ========================================
// コンポーネント
// ========================================

function DeviationChart({ data, threshold }: DeviationChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgWidth, setSvgWidth] = useState(600);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // データを日付順にソート
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [data]);

  // レスポンシブ: コンテナ幅に合わせてSVG幅を調整
  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      const w = containerRef.current.clientWidth;
      if (w > 0) setSvgWidth(w);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, [updateWidth]);

  // SVG高さは固定（横幅に対してアスペクト比を保つことも可能だが、固定のほうが見やすい）
  const svgHeight = 300;

  // 描画エリアの計算
  const chartWidth = svgWidth - PADDING.left - PADDING.right;
  const chartHeight = svgHeight - PADDING.top - PADDING.bottom;

  // Y軸の範囲を計算
  const deviations = sortedData.map((d) => d.averageDeviationMm);
  const dataMin = Math.min(...deviations);
  const dataMax = Math.max(...deviations);

  // 0を含むように範囲を拡張
  const yMin = Math.min(dataMin, 0);
  const yMax = Math.max(dataMax, 0);

  // マージンを追加
  const yRange = yMax - yMin || 1;
  const yMinWithMargin = yMin - yRange * 0.1;
  const yMaxWithMargin = yMax + yRange * 0.1;

  const yTicks = computeYTicks(yMinWithMargin, yMaxWithMargin);
  const yTickMin = yTicks[0];
  const yTickMax = yTicks[yTicks.length - 1];

  // スケール関数
  const xScale = (index: number): number => {
    if (sortedData.length === 1) return PADDING.left + chartWidth / 2;
    return PADDING.left + (index / (sortedData.length - 1)) * chartWidth;
  };

  const yScale = (value: number): number => {
    // SVGは上が0なので反転
    const ratio = (value - yTickMin) / (yTickMax - yTickMin);
    return PADDING.top + chartHeight * (1 - ratio);
  };

  // 折れ線パス
  const linePath = sortedData
    .map((d, i) => {
      const x = xScale(i);
      const y = yScale(d.averageDeviationMm);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // 0ラインのY座標
  const zeroY = yScale(0);

  // ホバーハンドラ
  const handleMouseEnter = (index: number) => setHoveredIndex(index);
  const handleMouseLeave = () => setHoveredIndex(null);

  // ツールチップの位置計算
  const getTooltipTransform = (index: number): string => {
    const x = xScale(index);
    const y = yScale(sortedData[index].averageDeviationMm);
    // ツールチップが右端からはみ出す場合は左に表示
    const isRightHalf = x > PADDING.left + chartWidth / 2;
    const offsetX = isRightHalf ? -130 : 10;
    const offsetY = -10;
    return `translate(${x + offsetX}, ${y + offsetY})`;
  };

  return (
    <div ref={containerRef} className="w-full">
      <svg
        width={svgWidth}
        height={svgHeight}
        className="select-none"
        role="img"
        aria-label="偏差の経過グラフ"
      >
        {/* Y軸グリッドラインと目盛り */}
        {yTicks.map((tick) => {
          const y = yScale(tick);
          const isZero = tick === 0;
          return (
            <g key={`y-${tick}`}>
              <line
                x1={PADDING.left}
                y1={y}
                x2={PADDING.left + chartWidth}
                y2={y}
                stroke={isZero ? ZERO_LINE_COLOR : GRID_COLOR}
                strokeWidth={isZero ? 1.5 : 1}
                strokeDasharray={isZero ? undefined : '4 2'}
              />
              <text
                x={PADDING.left - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className="text-xs fill-gray-500"
                fontSize={11}
              >
                {tick.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Y軸ラベル */}
        <text
          x={14}
          y={PADDING.top + chartHeight / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(-90, 14, ${PADDING.top + chartHeight / 2})`}
          className="fill-gray-600"
          fontSize={11}
        >
          平均偏差 (mm)
        </text>

        {/* X軸の目盛りラベル */}
        {sortedData.map((d, i) => {
          const x = xScale(i);
          // データが多い場合はラベルを間引く
          const maxLabels = Math.floor(chartWidth / 60);
          const step = Math.max(1, Math.ceil(sortedData.length / maxLabels));
          if (sortedData.length > maxLabels && i % step !== 0 && i !== sortedData.length - 1) {
            return null;
          }
          return (
            <text
              key={`x-${i}`}
              x={x}
              y={PADDING.top + chartHeight + 18}
              textAnchor="middle"
              dominantBaseline="hanging"
              className="fill-gray-500"
              fontSize={10}
              transform={`rotate(-30, ${x}, ${PADDING.top + chartHeight + 18})`}
            >
              {formatDateShort(d.date)}
            </text>
          );
        })}

        {/* 折れ線 */}
        {sortedData.length > 1 && (
          <path
            d={linePath}
            fill="none"
            stroke={LINE_COLOR}
            strokeWidth={2}
            strokeLinejoin="round"
          />
        )}

        {/* データポイント */}
        {sortedData.map((d, i) => {
          const x = xScale(i);
          const y = yScale(d.averageDeviationMm);
          const hasAbnormal = d.abnormalTrials > 0;
          const isHovered = hoveredIndex === i;
          return (
            <circle
              key={`point-${i}`}
              cx={x}
              cy={y}
              r={isHovered ? POINT_HOVER_RADIUS : POINT_RADIUS}
              fill={hasAbnormal ? ABNORMAL_POINT_COLOR : POINT_COLOR}
              stroke="white"
              strokeWidth={2}
              className="cursor-pointer transition-all duration-150"
              onMouseEnter={() => handleMouseEnter(i)}
              onMouseLeave={handleMouseLeave}
              onTouchStart={() => handleMouseEnter(i)}
              onTouchEnd={handleMouseLeave}
            />
          );
        })}

        {/* 0ライン強調（グリッドの上に描画） */}
        {yTickMin < 0 && yTickMax > 0 && (
          <line
            x1={PADDING.left}
            y1={zeroY}
            x2={PADDING.left + chartWidth}
            y2={zeroY}
            stroke={ZERO_LINE_COLOR}
            strokeWidth={1.5}
          />
        )}

        {/* ツールチップ */}
        {hoveredIndex !== null && sortedData[hoveredIndex] && (
          <g transform={getTooltipTransform(hoveredIndex)}>
            <rect
              x={0}
              y={-40}
              width={120}
              height={62}
              rx={4}
              fill="white"
              stroke="#d1d5db"
              strokeWidth={1}
              filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))"
            />
            <text x={8} y={-22} fontSize={10} className="fill-gray-600">
              {formatDateFull(sortedData[hoveredIndex].date)}
            </text>
            <text x={8} y={-6} fontSize={12} fontWeight="bold" className="fill-gray-900">
              {sortedData[hoveredIndex].averageDeviationMm.toFixed(2)} mm
            </text>
            <text x={8} y={10} fontSize={10} className="fill-gray-500">
              ({sortedData[hoveredIndex].averageDeviationPercent.toFixed(2)}%)
            </text>
          </g>
        )}
      </svg>

      {/* 凡例 */}
      <div className="flex flex-wrap items-center gap-4 mt-2 px-2 text-xs text-gray-600">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: POINT_COLOR }}
          />
          <span>正常</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: ABNORMAL_POINT_COLOR }}
          />
          <span>異常試行あり</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-6 border-t-2"
            style={{ borderColor: LINE_COLOR }}
          />
          <span>偏差推移</span>
        </div>
        {threshold !== undefined && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">
              異常閾値: {threshold}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviationChart;
